import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Info, Download, Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown, X, TrendingUp, TrendingDown, MapPin, Map, Grid3X3, Target, Crosshair, IndianRupee, ArrowRightLeft, Clock, Landmark, Wallet, Scale, ArrowUpRight, Sparkles, Users } from "lucide-react";
import IndiaMap from "../components/IndiaMap";
import ThemeChip from "../components/ThemeChip";
import { fmtCr, fmtNum, displayTheme } from "../utils/format";
import { useCommandCentreData } from "../hooks/useCommandCentreData";
import { useAuth } from "../context/AuthContext";
import { useFilters } from "../context/FilterContext";

const PIE_COLORS = [
  "#B71C1C", // deep red       — Cancer Care
  "#16A34A", // green          — Rural Upliftment
  "#F59E0B", // amber/yellow   — Education
  "#475569", // slate gray     — Institutions
  "#8B5CF6", // violet         — Health
  "#0EA5E9", // sky blue       — Nutrition
  "#EA580C", // burnt orange   — Others
  "#0D9488", // teal           — Water & Sanitation
  "#DB2777", // pink / magenta — Urban Poverty
  "#4F46E5", // indigo         — Arts, Culture
];

/* ─── YoY Delta Badge ───────────────────────────────────────────── */
function YoyBadge({ delta, prevFy }) {
  if (!delta || delta.pct === null || delta.pct === undefined) return null;
  const pct = delta.pct;
  const isUp = pct > 0;
  const isDown = pct < 0;
  const isFlat = pct === 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : null;
  const color = isUp ? "text-green-600" : isDown ? "text-red-500" : "text-slate-600";
  const bg = isUp ? "bg-green-50" : isDown ? "bg-red-50" : "bg-slate-50";
  const label = `${isUp ? "+" : ""}${pct.toFixed(1)}%`;
  return (
    <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full ${bg} ${color}`} data-testid="yoy-badge" title={`vs ${prevFy || "last FY"}`}>
      {Icon && <Icon size={10} strokeWidth={2} />}
      <span className="text-[9px] font-semibold leading-none">{isFlat ? "0%" : label}</span>
    </div>
  );
}

export default function CommandCentre() {
  const navigate = useNavigate();
  const location = useLocation();
  // Raw mode = Real-Executive route. When active, we zero-out every Finance
  // uplift (Sanctioned boost, Strategic Initiatives slice, Lapsed, Under
  // Closure) so the UI shows pure live Snowflake numbers coming through the
  // `raw` schema via the axios ?raw=true interceptor. Only data-visualisation
  // flags are affected; no visual layout changes.
  const isRawMode = location.pathname.startsWith("/real-");
  const {
    summary, geoSummary, stateData, allocationByTheme,
    ragSummary, loading, sf, s, hasGrants, g, totalSanctioned,
    selectedState, selectedDistrict, districtData, blockData, mapLoading,
    handleStateSelect, handleDistrictSelect,
    regionSummary, regionGeoSummary, regionRagSummary,
    aspirationalData,
    sankeyData,
    mapViewMode, setMapViewMode, allDistrictData,
    yoyData,
    grants, grantsLoading, grantsPage, setGrantsPage, grantsTotal,
    grantsTotalPages, grantsSearchInput, setGrantsSearchInput,
    grantsSortBy, grantsSortOrder, grantsExporting,
    handleGrantsSort, handleGrantsSearch, clearGrantsSearch,
    handleGrantsExport, scrollToGrantsTable, grantsTableRef,
    grantsTableOpen, setGrantsTableOpen,
    filters, filterOptions, viewMode, API,
  } = useCommandCentreData();

  const statusClass = (s) =>
    s === "Active" || s === "Ongoing" || s === "Under Closure" ? "bg-green-100 text-green-700" :
    s === "Closed" ? "bg-slate-100 text-slate-500" :
    "bg-amber-100 text-amber-700";

  // Only show YoY badges when a specific FY is selected
  const yoy = filters.fy ? yoyData : null;

  // ── FY 2025-26 / FY 2026-27 adjustments (per stakeholder, Feb 2026, revised Feb 9) ────
  // When ONLY FY 2025-26 OR ONLY FY 2026-27 + All India is selected AND view-mode is NOT "New":
  //   • Sanctioned = raw query value + 19.62 Cr + 1,051.53 Cr  (= +1,071.15 Cr)
  //   • Lapsed Commitment   = ₹111.66 Cr (static, hidden from UI, used in Balance calc)
  //   • Under Closure       = ₹349.16 Cr (static, hidden from UI, used in Balance calc)
  //   • Balance Commitment  = Sanctioned − 111.66 − 349.16 − Disbursement (Cumulative)
  // For "New" view-mode the entire boost is suppressed (only Sanctioned
  // + Disbursement tiles render) because the static figures represent prior-year
  // commitments that do not belong to new grants.
  const isOnlyFy2526 = Array.isArray(filters.fy) && filters.fy.length === 1 && filters.fy[0] === "FY 2025-26";
  const isOnlyFy2627 = Array.isArray(filters.fy) && filters.fy.length === 1 && filters.fy[0] === "FY 2026-27";
  // "All India" = no state or district has been drilled into on the map.
  // When a state/district is selected, the boost and Strategic Initiatives slice
  // are NOT applied (per stakeholder direction, Jun 2026).
  const isAllIndia = !selectedState && !selectedDistrict;
  // viewMode "snapshot" = New, "ongoing" = Ongoing, anything else = All Grants
  const isNewView = viewMode === "snapshot";
  // Status filter: "" = All, "Active" = Active, "Closed" = Closed.
  // Per stakeholder (Feb 2026):
  //   • Active / Closed → only Sanctioned + Disbursed tiles render (no Lapsed/Under-Closure/Balance).
  //   • Closed → Sanctioned must NOT receive the +936.56 Cr static boost
  //     (those represent in-progress bulk approvals / strategic initiatives).
  const statusFilter = filters.status || "";   // "", "Active", or "Closed"
  const isStatusActive = statusFilter === "Active";
  const isStatusClosed = statusFilter === "Closed";
  // Per stakeholder (Feb 2026): selecting any specific theme / fund (trust) /
  // grantee also suppresses the FY 25-26 adjustments — the +936.56 Cr boost
  // represents portfolio-wide bulk approvals & strategic initiatives that
  // cannot be attributed to a single theme/trust/grantee.
  const hasSpecificFilter = Boolean(filters.theme || filters.trust || filters.grantee);
  // Ch7 (Sep 10, 2026): 5-tile split card layout renders on FY 25-26 or FY 26-27 for
  // EVERY combination of view_mode (All / New / Ongoing) × status (All / Active / Closed)
  // × theme/trust/grantee. Only the SANCTIONED ADD-ON is guardrail-gated (see below).
  const applyFy2526Adjustments = !isRawMode && isOnlyFy2526 && isAllIndia && !isNewView && !isStatusActive && !isStatusClosed && !hasSpecificFilter;
  const applySplitCards = !isRawMode && (isOnlyFy2526 || isOnlyFy2627);
  // Sanctioned add-on rule (Ch5 revised, Sep 10, 2026): FY-aware amount.
  //   • Sole FY 24-25 or 25-26 → 1,071.15 Cr add-on (19.62 + 1,051.53).
  //   • FY 26-27 / multi-FY / All Years → 936.56 Cr add-on (11.34 + 925.22).
  // Semantic guardrails: suppressed under theme/trust/grantee, state/district
  // drill-down, "New" view, or Status = Closed (see Q8 answer, Sep 10, 2026).
  const fyArrList = Array.isArray(filters.fy) ? filters.fy : (filters.fy ? [filters.fy] : []);
  const isOnlyFy2425 = fyArrList.length === 1 && fyArrList[0] === "FY 2024-25";
  // Ch19 (Sep 10, 2026): "All Years" = 3+ FYs selected. Only in this scope do we
  // drop the "Year till date" sub-line from the default 2-card Disbursement tile,
  // matching Trustee's mirrorExecutive treatment. Multi-FY with <3 selections and
  // any single-FY view keep the dual display.
  const isAllYears = fyArrList.length >= 3;
  const applySanctionedBoost = !isRawMode && !isOnlyFy2425 && isAllIndia && !isNewView && !isStatusClosed && !hasSpecificFilter;
  const upliftIsFy2526Variant = isOnlyFy2526 || isOnlyFy2425;
  const LAPSED_CR = isRawMode ? 0 : 111.66;
  const UNDER_CLOSURE_CR = isRawMode ? 0 : 349.16;
  const SANCTIONED_BOOST_A = isRawMode ? 0 : (upliftIsFy2526Variant ? 19.62 : 11.34);
  const SANCTIONED_BOOST_B = isRawMode ? 0 : (upliftIsFy2526Variant ? 1051.53 : 925.22);
  const FY2526_SANCTIONED_BOOST = SANCTIONED_BOOST_A + SANCTIONED_BOOST_B; // 1,071.15 (25-26 variant) or 936.56 (26-27 / multi-FY)
  const STRATEGIC_INITIATIVES_CR = SANCTIONED_BOOST_B;
  const baseSanctioned = s?.total_allocation_cr || s?.allocation_cr || 0;
  const cumDisbursement = s?.cumulative_disbursement_cr || 0;
  const disbursedForFy = s?.total_disbursement_cr || s?.disbursement_cr || 0;
  const sanctionedDisplay = applySanctionedBoost ? baseSanctioned + FY2526_SANCTIONED_BOOST : baseSanctioned;
  // Balance Commitment formula:
  //   FY 25-26 → Sanctioned − Lapsed − Under Closure − Disbursement (Cumulative)
  //   FY 26-27 → Sanctioned − Disbursement (Cumulative) — Lapsed & Under Closure
  //              are excluded per stakeholder direction (Feb 9, 2026).
  const balanceCommitment = isOnlyFy2627
    ? (sanctionedDisplay - cumDisbursement)
    : (sanctionedDisplay - LAPSED_CR - UNDER_CLOSURE_CR - cumDisbursement);
  const sanctionedByThemeTotal = applySanctionedBoost ? totalSanctioned + FY2526_SANCTIONED_BOOST : totalSanctioned;

  // Snapshot Sanctioned (Year till date) — "grants approved in FY".
  // Matches the Trustee Overview formula so the two dashboards stay in sync.
  // Ch10 (Sep 10, 2026): FY 26-27 is FINANCE-LOCKED to ₹528.74 Cr, overriding the live snapshot.
  const { API: API_SNAPSHOT } = useFilters();
  const [sanctionedYtdCr, setSanctionedYtdCr] = useState(null);
  const FY2627_SANCTIONED_YTD_LOCKED_CR = 528.74;   // Finance-approved static — do not modify without direct user instruction.
  useEffect(() => {
    if (!applySplitCards) { setSanctionedYtdCr(null); return; }
    if (isOnlyFy2627) { setSanctionedYtdCr(FY2627_SANCTIONED_YTD_LOCKED_CR); return; }
    const fyParam = isOnlyFy2526 ? "FY 2025-26" : "FY 2026-27";
    axios.get(`${API_SNAPSHOT}/executive/summary`, { params: { fy: fyParam, view_mode: "snapshot" } })
      .then(r => setSanctionedYtdCr(r.data?.total_allocation_cr || 0))
      .catch(() => setSanctionedYtdCr(0));
  }, [API_SNAPSHOT, applySplitCards, isOnlyFy2526, isOnlyFy2627]);

  // Personalised greeting (time-of-day aware) — shown only on first paint per session
  const { user } = useAuth();
  const firstName = (user?.name || user?.email || "").split(/[\s@]/)[0] || "";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "–";

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
      </div>
    );
  }

  return (
    <div data-testid="command-centre" className="space-y-3">
      {/* Main Content: Map + Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-3 items-start">

        {/* LEFT: India Map */}
        <div className="min-w-0 overflow-hidden">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-3 overflow-hidden flex justify-center">
              {sf ? (
                <IndiaMap
                  stateData={stateData}
                  districtData={districtData}
                  blockData={blockData}
                  allDistrictData={allDistrictData}
                  onStateSelect={handleStateSelect}
                  onDistrictSelect={handleDistrictSelect}
                  selectedState={selectedState}
                  selectedDistrict={selectedDistrict}
                  dataLoading={mapLoading}
                  mapViewMode={mapViewMode}
                  onMapViewChange={(mode) => { setMapViewMode(mode); if (selectedState) handleStateSelect(null); }}
                />
              ) : (
                <div className="h-64 flex items-center justify-center bg-slate-50 rounded-lg text-slate-600">
                  Map requires data connection
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Metrics Panel (1/3 width) — compact */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-3 flex flex-col gap-2">

            {/* Grants Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Grants</h3>
                <button onClick={scrollToGrantsTable} className="text-[10px] text-[#B71C1C] hover:underline" data-testid="grants-view-scroll">View →</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-[#B71C1C]/30 transition-colors"
                  data-testid="kpi-grants-approved"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#B71C1C] flex items-center justify-center flex-shrink-0">
                    <FileText size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.total_grants || s?.grants)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{viewMode === "snapshot" ? "New Grants" : viewMode === "active" ? "All Grants" : "Ongoing Grants"}</div>
                  </div>
                </div>
                <div
                  onClick={scrollToGrantsTable}
                  className="flex items-center gap-2 p-2.5 bg-white rounded-lg border border-slate-100 cursor-pointer hover:border-green-300 transition-colors"
                  data-testid="kpi-grantees"
                >
                  <div className="w-9 h-9 rounded-lg bg-green-600 flex items-center justify-center flex-shrink-0">
                    <Users size={18} className="text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xl font-bold text-slate-900 leading-none">{fmtNum(s?.active_grantees || s?.grantees)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">Grantees</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-slate-50 rounded-lg p-2.5">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Financial</h3>
                {s?.is_apportioned && (
                  <div className="group relative">
                    <Info size={13} className="text-amber-500 cursor-help" data-testid="apportionment-info-icon" />
                    <div className="absolute right-0 top-5 z-50 hidden group-hover:block w-64 p-2.5 bg-slate-800 text-white text-[10px] leading-relaxed rounded-lg shadow-lg">
                      Amounts are proportionally estimated. Multi-state grants are split by block/district count in this region.
                    </div>
                  </div>
                )}
              </div>
              {(() => {
                // ── FY 25-26 / FY 26-27 split-card layout (Sep 10, 2026 request) ─────
                //   Row 1: Sanctioned (Cumulative)    │ Disbursed (Cumulative)
                //   Row 2: Sanctioned (Year till date)│ Disbursed (Year till date)
                //   Row 3: Balance Commitment (full width)
                if (applySplitCards) {
                  return (
                    <div className="grid grid-cols-2 gap-2">
                      {/* Row 1a: Sanctioned (Cumulative) — with uplift + info tooltip */}
                      <div
                        className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-2 border border-blue-100 flex flex-col justify-start min-h-[68px] relative group"
                        data-testid="kpi-sanctioned-cumulative"
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100"><IndianRupee size={11} className="text-blue-600" /></div>
                          <span className="text-[10px] text-blue-700 font-medium leading-tight">Sanctioned (Cumulative)</span>
                          {applySanctionedBoost && (
                            <>
                              <Info size={10} className="text-blue-400 cursor-help" />
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate("/financial#bulk-balance"); }}
                                className="ml-auto p-0.5 -m-0.5 rounded hover:bg-blue-100 transition-colors"
                                title="Go to Strategic Initiatives breakup on Financial page"
                                data-testid="sanctioned-link-arrow"
                              >
                                <ArrowUpRight size={11} className="text-blue-500 hover:text-blue-700" />
                              </button>
                            </>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); scrollToGrantsTable(); }}
                          className="text-left text-lg font-bold text-slate-900 hover:text-blue-700 transition-colors cursor-pointer"
                          data-testid="kpi-sanctioned-cumulative-number"
                          title="View the underlying grants list"
                        >
                          {fmtCr(sanctionedDisplay)}
                        </button>
                        {applySanctionedBoost && (
                          <div
                            className="absolute left-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 pointer-events-none"
                            style={{ width: "max-content", maxWidth: "340px" }}
                            data-testid="sanctioned-tooltip"
                          >
                            <span className="font-bold text-[11px] block mb-1">Sanctioned (Cumulative)</span>
                            <span className="block">= Total Sanctioned + Grants in Progress + Strategic Initiatives</span>
                            <span className="block mt-1">= {fmtCr(baseSanctioned)} + ₹{SANCTIONED_BOOST_A.toFixed(2)} Cr + ₹{SANCTIONED_BOOST_B.toFixed(2)} Cr</span>
                            <span className="block font-bold mt-1">= {fmtCr(sanctionedDisplay)}</span>
                          </div>
                        )}
                      </div>

                      {/* Row 1b: Disbursed (Cumulative) — Ch12: FY 26-27 subtext */}
                      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2 border border-emerald-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-disbursed-cumulative">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                          <span className="text-[10px] text-emerald-700 font-medium leading-tight">Disbursed (Cumulative)</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900" data-testid="kpi-disbursed-cumulative-number">{fmtCr(cumDisbursement)}</span>
                        {isOnlyFy2627 && (
                          <span className="text-[9px] text-slate-500 mt-0.5 leading-tight" data-testid="kpi-disbursed-cumulative-subtext">
                            205 Cr under process; 53 Cr under Individual grants disbursed
                          </span>
                        )}
                      </div>

                      {/* Row 2a: Sanctioned (Year till date) — grants approved in FY.
                          Ch10: FY 26-27 is Finance-locked to ₹528.74 Cr.
                          Ch11: FY 26-27 subtext caption. */}
                      <div className="bg-gradient-to-br from-indigo-50 to-white rounded-lg p-2 border border-indigo-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-sanctioned-ytd">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-indigo-100"><IndianRupee size={11} className="text-indigo-600" /></div>
                          <span className="text-[10px] text-indigo-700 font-medium leading-tight">Sanctioned (Year till date)</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900" data-testid="kpi-sanctioned-ytd-number">
                          {sanctionedYtdCr == null ? "…" : fmtCr(sanctionedYtdCr)}
                        </span>
                        <span className="text-[9px] text-slate-500 mt-0.5">grants approved in FY</span>
                        {isOnlyFy2627 && (
                          <span className="text-[9px] text-slate-500 mt-0.5 leading-tight" data-testid="kpi-sanctioned-ytd-subtext">
                            Additional circular worth 301.98 Cr sanctioned &amp; under grant letter process
                          </span>
                        )}
                      </div>

                      {/* Row 2b: Disbursed (Year till date) */}
                      <div className="bg-gradient-to-br from-violet-50 to-white rounded-lg p-2 border border-violet-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-disbursed-ytd">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-violet-100"><Wallet size={11} className="text-violet-600" /></div>
                          <span className="text-[10px] text-violet-700 font-medium leading-tight">Disbursed (Year till date)</span>
                        </div>
                        <span className="text-lg font-bold text-slate-900" data-testid="kpi-disbursed-ytd-number">{fmtCr(disbursedForFy)}</span>
                      </div>

                      {/* Row 3: Balance Commitment (full width) — Ch16 (Sep 10, 2026):
                          suppressed for FY 26-27; only rendered for FY 25-26 (all its
                          filter combos). FY 26-27 shows just the 2×2 top grid. */}
                      {!isOnlyFy2627 && (
                      <div
                        className="col-span-2 bg-gradient-to-br from-cyan-50 to-white rounded-lg p-2 border border-cyan-100 flex flex-col justify-start min-h-[68px] cursor-pointer hover:shadow-md transition-shadow relative group"
                        data-testid="kpi-balance-commitment"
                        onClick={() => navigate("/financial#balance-commitment")}
                      >
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="w-5 h-5 rounded flex items-center justify-center bg-cyan-100"><Landmark size={11} className="text-cyan-600" /></div>
                          <span className="text-[10px] text-cyan-700 font-medium">Balance Commitment</span>
                          <Info size={10} className="text-cyan-400 cursor-help" />
                          <ArrowUpRight size={10} className="text-cyan-400 ml-auto" />
                        </div>
                        <span className="text-lg font-bold text-slate-900">{fmtCr(balanceCommitment)}</span>
                        <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 whitespace-nowrap pointer-events-none">
                          <span className="font-bold text-[11px] block mb-1">Balance Commitment</span>
                          <span className="block">= Sanctioned − Lapsed − Under Closure − Disbursement (Cumulative)</span>
                          <span className="block mt-1">= {fmtCr(sanctionedDisplay)} − ₹{LAPSED_CR} Cr − ₹{UNDER_CLOSURE_CR} Cr − {fmtCr(cumDisbursement)}</span>
                          <span className="block font-bold mt-1">= {fmtCr(balanceCommitment)}</span>
                        </div>
                      </div>
                      )}
                    </div>
                  );
                }
                // ── Default layout (all other FY/filter combinations) ────────────────
                return (
              <div className="grid grid-cols-2 gap-2">
                {/* 1. Sanctioned — adjusted with +11.34 +925.22 only when FY 2025-26 AND All India AND not "New".
                    Click behavior:
                      • Number    → scroll to in-page Grants table (always, when grants table exists)
                      • Arrow ↗  → navigate to /financial#bulk-balance (only when boost applied) */}
                <div
                  className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-2 border border-blue-100 flex flex-col justify-start min-h-[68px] relative group"
                  data-testid="kpi-sanctioned"
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-blue-100"><IndianRupee size={11} className="text-blue-600" /></div>
                    <span className="text-[10px] text-blue-700 font-medium">Sanctioned</span>
                    {s?.is_apportioned && <span className="text-[8px] text-amber-500 font-medium">~est.</span>}
                    {applySanctionedBoost && (
                      <>
                        <Info size={10} className="text-blue-400 cursor-help" data-testid="sanctioned-info-icon" />
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate("/financial#bulk-balance"); }}
                          className="ml-auto p-0.5 -m-0.5 rounded hover:bg-blue-100 transition-colors"
                          title="Go to Strategic Initiatives breakup on Financial page"
                          data-testid="sanctioned-link-arrow"
                        >
                          <ArrowUpRight size={11} className="text-blue-500 hover:text-blue-700" />
                        </button>
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); scrollToGrantsTable(); }}
                    className="text-left text-lg font-bold text-slate-900 hover:text-blue-700 transition-colors cursor-pointer"
                    title="View the underlying grants list"
                    data-testid="kpi-sanctioned-number"
                  >
                    {fmtCr(sanctionedDisplay)}
                  </button>
                  {applySanctionedBoost && (
                    <div
                      className="absolute left-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 pointer-events-none"
                      style={{ width: "max-content", maxWidth: "340px" }}
                      data-testid="sanctioned-tooltip"
                    >
                      <span className="font-bold text-[11px] block mb-1">Sanctioned Calculation</span>
                      <span className="block">= Total Sanctioned + Grants in Progress + Strategic Initiatives</span>
                      <span className="block mt-1">= {fmtCr(baseSanctioned)} + ₹{SANCTIONED_BOOST_A.toFixed(2)} Cr + ₹{SANCTIONED_BOOST_B.toFixed(2)} Cr</span>
                      <span className="block font-bold mt-1">= {fmtCr(sanctionedDisplay)}</span>
                      <span className="block mt-1.5 text-blue-200 italic">Click the amount to view the underlying grants ↓ · Arrow ↗ opens the Strategic Initiatives breakup on Financial.</span>
                    </div>
                  )}
                </div>

                {/* 2. Disbursement — dual display for single/multi-FY; single "Till date" for All Years (Ch19). */}
                <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-2 border border-emerald-100 flex flex-col justify-start min-h-[68px]" data-testid="kpi-cumulative-disbursement">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-emerald-100"><ArrowRightLeft size={11} className="text-emerald-600" /></div>
                    <span className="text-[10px] text-emerald-700 font-medium leading-tight">
                      Disbursement{isAllYears && <span className="text-emerald-500 font-normal"> (till date, cumulative)</span>}
                    </span>
                  </div>
                  {isAllYears ? (
                    <span className="text-lg font-bold text-slate-900" data-testid="kpi-disbursement-till-date">{fmtCr(cumDisbursement)}</span>
                  ) : (
                    <div className="space-y-0.5">
                      <div className="flex items-baseline justify-between gap-2 whitespace-nowrap" data-testid="kpi-disbursement-till-date">
                        <span className="text-[9px] text-slate-500 normal-case tracking-normal">Till date <span className="text-slate-400">(cumulative)</span></span>
                        <span className="text-sm font-bold text-slate-900">{fmtCr(cumDisbursement)}</span>
                      </div>
                      <div className="flex items-baseline justify-between gap-2 pt-0.5 border-t border-emerald-100 whitespace-nowrap" data-testid="kpi-disbursement-ytd">
                        <span className="text-[9px] text-slate-500 normal-case tracking-normal">Year till date</span>
                        <span className="text-sm font-bold text-slate-900">{fmtCr(disbursedForFy)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
                );
              })()}
            </div>

            {/* Geographic Stats */}
            <div className="bg-slate-50 rounded-lg p-2.5" data-testid="geographic-card">
              <div className="flex items-center justify-between mb-1.5">
                <h3 className="font-semibold text-slate-700 text-xs uppercase tracking-wide">Geographic</h3>
                <button onClick={() => navigate('/geographic')} className="text-[10px] text-[#B71C1C] hover:underline">Map →</button>
              </div>
              <div className="space-y-1.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-blue-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.states) || '–'}</div>
                      <div className="text-[10px] text-slate-500">States & UT</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-teal-50 flex items-center justify-center flex-shrink-0">
                      <Map size={14} className="text-teal-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.districts) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Districts</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <div className="bg-white rounded-lg p-2 border border-slate-100 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Grid3X3 size={14} className="text-slate-500" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-slate-800 leading-tight">{fmtNum(g?.blocks) || '–'}</div>
                      <div className="text-[10px] text-slate-500">Blocks</div>
                    </div>
                  </div>
                  <div className="bg-amber-50 rounded-lg p-2 border border-amber-200 flex items-center gap-2" data-testid="asp-districts-card">
                    <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Target size={14} className="text-amber-600" />
                    </div>
                    <div>
                      <div className="text-lg font-bold text-amber-700 leading-tight">{aspirationalData.ad?.covered || 0}/{aspirationalData.ad?.total || 0}</div>
                      <div className="text-[10px] text-slate-500">Asp. Districts</div>
                    </div>
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 border border-purple-200 flex items-center gap-2 justify-center" data-testid="asp-blocks-card">
                  <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Crosshair size={14} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-purple-700 leading-tight">{aspirationalData.ab?.covered || 0}/{aspirationalData.ab?.total || 0}</div>
                    <div className="text-[10px] text-slate-500">Aspirational Blocks</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Sanctioned by Theme — full-width row below Map + Side Panel */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="allocation-by-theme-section">
        <div className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-baseline justify-between">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{sf ? "Sanctioned by Theme" : "Disbursement by Theme"}</h3>
            <p className="text-xs text-slate-500 mt-0.5">% share of portfolio · {`₹${sanctionedByThemeTotal.toFixed(2)} Cr total`}</p>
          </div>
        </div>
        <div className="p-4">
          {hasGrants && allocationByTheme.length > 0 ? (
            <ThemePortfolioBars
              data={allocationByTheme}
              sf={sf}
              extraSegment={applyFy2526Adjustments ? { theme: "Strategic Initiatives", value: STRATEGIC_INITIATIVES_CR } : null}
            />
          ) : <div className="py-8 text-center text-sm text-slate-600">{loading ? "Loading..." : "No data for selected filters"}</div>}
        </div>
      </div>

      {/* Row 2: Source of Fund × Portfolio Heatmap moved to Finance page */}

      {/* ── Sankey: Sanctioned Amount Flow (hidden) ────────────────── */}

      {/* ── Inline Grants Table ─────────────────────────────────────── */}
      <div ref={grantsTableRef} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="exec-grants-table-section">
        <div
          className="px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between cursor-pointer select-none"
          onClick={() => setGrantsTableOpen(!grantsTableOpen)}
          data-testid="exec-grants-table-toggle"
        >
          <div>
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              Grants
              <ChevronDown size={14} className={`text-slate-600 transition-transform ${grantsTableOpen ? "rotate-180" : ""}`} />
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <p className="text-xs text-slate-500">
                {grantsTotal.toLocaleString()} grants
                {s && (
                  <>
                    <span className="mx-1.5 text-slate-300">·</span>
                    <span className="text-blue-700 font-semibold tabular-nums" data-testid="grants-table-total-sanctioned">
                      {fmtCr(baseSanctioned)} sanctioned
                    </span>
                    {applySanctionedBoost && (
                      <span
                        className="ml-1 text-[11px] text-slate-500 group relative cursor-help"
                        data-testid="grants-table-uplift-note"
                      >
                        (+ {fmtCr(FY2526_SANCTIONED_BOOST)} Grants in Progress & Strategic Initiatives = <span className="font-semibold text-slate-700">{fmtCr(sanctionedDisplay)} total</span>)
                        <span
                          className="absolute left-0 top-full mt-1 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-3 py-2 shadow-lg z-40 pointer-events-none whitespace-normal"
                          style={{ width: "max-content", maxWidth: "320px" }}
                        >
                          The {fmtCr(baseSanctioned)} above is the sum of the {grantsTotal.toLocaleString()} individual grants listed. The Sanctioned KPI adds a portfolio-wide {fmtCr(FY2526_SANCTIONED_BOOST)} for Grants in Progress ({fmtCr(SANCTIONED_BOOST_A)}) and Strategic Initiatives ({fmtCr(SANCTIONED_BOOST_B)}), which don't appear as individual rows.
                        </span>
                      </span>
                    )}
                    <span className="mx-1.5 text-slate-300">·</span>
                    <span className="text-emerald-700 font-semibold tabular-nums" data-testid="grants-table-total-disbursed">
                      {fmtCr(cumDisbursement)} disbursed
                    </span>
                  </>
                )}
              </p>
              {grantsTableOpen && <span className="text-[11px] text-[#B71C1C] font-semibold">Click on Grant ID to view more details</span>}
              {selectedState && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-700 rounded text-[10px] font-medium" data-testid="grants-state-filter-tag">
                  {selectedState}{selectedDistrict ? ` / ${selectedDistrict}` : ""}
                </span>
              )}
            </div>
          </div>
          {grantsTableOpen && (
            <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
              <form onSubmit={handleGrantsSearch} className="flex items-center gap-1.5">
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-600" />
                  <input
                    type="text"
                    value={grantsSearchInput}
                    onChange={(e) => setGrantsSearchInput(e.target.value)}
                    placeholder="Search grants..."
                    className="pl-8 pr-7 py-1.5 border border-slate-200 rounded-lg text-xs w-56 focus:outline-none focus:ring-2 focus:ring-[#B71C1C]/20 focus:border-[#B71C1C]"
                    data-testid="exec-grants-search"
                  />
                  {grantsSearchInput && (
                    <button type="button" onClick={clearGrantsSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <button type="submit" className="px-2.5 py-1.5 bg-[#B71C1C] text-white rounded-lg text-xs font-medium hover:bg-[#8B1515]" data-testid="exec-grants-search-btn">Search</button>
              </form>
              <button
                onClick={handleGrantsExport}
                disabled={grantsExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-60 transition-colors whitespace-nowrap"
                data-testid="exec-grants-export"
              >
                <Download size={13} />
                {grantsExporting ? "Exporting..." : "Export CSV"}
              </button>
            </div>
          )}
        </div>

        {grantsTableOpen && (
          <>
            {grantsLoading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#B71C1C]" />
              </div>
            ) : grants.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-600">
                <FileText size={40} className="mb-2 opacity-40" />
                <p className="text-sm">No grants found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="exec-grants-table">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50">
                      <SortTh label="Grant ID"     field="grant_id"           sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="px-3" />
                      <SortTh label="Project Title" field="project_title"     sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grantee"      field="grantee_name"       sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Portfolio"    field="theme"              sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Status"       field="status"             sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Sanctioned"   field="amount_recommended" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right" />
                      <SortTh label="Disbursed (Till Date)" field="disbursed"     sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right"
                        tooltip="Till Date = sum of all payments for this grant up to today's date, regardless of FY." />
                      <SortTh label="Disbursed (YTD)"       field="disbursed_ytd" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} className="text-right"
                        tooltip="YTD = payments made only in the FY selected in the top filter." />
                      {selectedState && (
                        <>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50" data-testid="col-sanctioned-geo">Sanctioned ({selectedDistrict || selectedState})</th>
                          <th className="text-left font-medium text-amber-600 uppercase py-2 px-2 whitespace-nowrap text-[10px] bg-amber-50/50" data-testid="col-disbursed-geo">Disbursed ({selectedDistrict || selectedState})</th>
                        </>
                      )}
                      <SortTh label="Start Date"   field="project_start_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="End Date"     field="project_end_date"   sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="FY"           field="fy"                 sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                      <SortTh label="Grant Approved Date" field="grant_approved_date" sortBy={grantsSortBy} sortOrder={grantsSortOrder} onSort={handleGrantsSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g, i) => (
                      <tr key={g.grant_id || i} className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${i % 2 === 0 ? "bg-white" : "bg-slate-50/30"}`}>
                        <td className="py-2 px-3 font-mono whitespace-nowrap">
                          <button onClick={() => navigate(`/grant-overview?grant=${g.grant_id}`)}
                            className="text-[#B71C1C] hover:underline cursor-pointer font-medium">{g.grant_id || "–"}</button>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="font-medium text-slate-800 max-w-[200px] truncate block" title={g.project_title}>{g.project_title || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className="text-slate-700 max-w-[140px] truncate block" title={g.grantee}>{g.grantee || "–"}</span>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">{g.theme ? <ThemeChip theme={g.theme} /> : "–"}</td>
                        <td className="py-2 px-2 whitespace-nowrap">
                          <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${statusClass(g.status)}`}>{g.status || "–"}</span>
                        </td>
                        <td className="py-2 px-2 text-right font-medium text-slate-800 whitespace-nowrap">{g.amount_cr ? fmtCr(g.amount_cr) : "–"}</td>
                        <td className="py-2 px-2 text-right font-medium text-green-700 whitespace-nowrap">{g.disbursed_cr ? fmtCr(g.disbursed_cr) : "–"}</td>
                        <td className="py-2 px-2 text-right font-medium text-emerald-800 whitespace-nowrap">{g.disbursed_ytd_cr ? fmtCr(g.disbursed_ytd_cr) : "–"}</td>
                        {selectedState && (
                          <>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30" data-testid="sanctioned-geo-val">{g.sanctioned_geo_cr != null ? fmtCr(g.sanctioned_geo_cr) : "–"}</td>
                            <td className="py-2 px-2 text-right font-medium text-amber-700 whitespace-nowrap bg-amber-50/30" data-testid="disbursed-geo-val">{g.disbursed_geo_cr != null ? fmtCr(g.disbursed_geo_cr) : "–"}</td>
                          </>
                        )}
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.start_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.end_date)}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{g.fy || "–"}</td>
                        <td className="py-2 px-2 text-slate-500 whitespace-nowrap">{fmtDate(g.approved_date)}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}

        {/* Pagination */}
        {grantsTableOpen && grantsTotalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50">
            <div className="text-xs text-slate-500">
              Showing {((grantsPage - 1) * 50) + 1}–{Math.min(grantsPage * 50, grantsTotal)} of {grantsTotal} grants
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setGrantsPage(p => Math.max(1, p - 1))} disabled={grantsPage === 1}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="exec-grants-prev">
                <ChevronLeft size={14} />
              </button>
              <span className="text-xs text-slate-600">Page {grantsPage} of {grantsTotalPages}</span>
              <button onClick={() => setGrantsPage(p => Math.min(grantsTotalPages, p + 1))} disabled={grantsPage === grantsTotalPages}
                className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100" data-testid="exec-grants-next">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── SortTh Sub-Component ────────────────────────────────────────── */
function SortTh({ label, field, sortBy, sortOrder, onSort, className = "", tooltip = "" }) {
  const active = sortBy === field;
  return (
    <th
      className={`text-left font-medium text-slate-500 uppercase py-2 px-2 whitespace-nowrap cursor-pointer select-none hover:text-slate-800 hover:bg-slate-100 transition-colors group ${className}`}
      onClick={() => onSort(field)}
      data-testid={`exec-sort-${field}`}
      title={tooltip || undefined}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {tooltip && <span className="text-slate-400 text-[10px]" data-testid={`th-tooltip-${field}`}>ⓘ</span>}
        <span className={`transition-colors ${active ? "text-[#B71C1C]" : "text-slate-500 group-hover:text-slate-600"}`}>
          {active
            ? sortOrder === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
            : <ChevronsUpDown size={12} />}
        </span>
      </span>
    </th>
  );
}


/* ─── ThemePortfolioBars: single horizontal stacked bar (segments per theme) ── */
const STRATEGIC_INITIATIVES_COLOR = "#7F1D1D"; // deep Tata red — distinct from theme palette

function ThemePortfolioBars({ data, sf, extraSegment = null }) {
  const valueKey = sf ? "allocation_cr" : "disbursed_cr";
  const sorted = [...data].sort((a, b) => (b[valueKey] || 0) - (a[valueKey] || 0));
  // Append the Strategic Initiatives segment last (its colour is fixed,
  // outside the theme palette, so it's always visually distinct).
  const segments = sorted.map((d, i) => ({
    theme: d.theme,
    value: Number(d[valueKey] || 0),
    color: PIE_COLORS[i % PIE_COLORS.length],
    isExtra: false,
  }));
  if (extraSegment && extraSegment.value > 0) {
    segments.push({
      theme: extraSegment.theme,
      value: Number(extraSegment.value),
      color: STRATEGIC_INITIATIVES_COLOR,
      isExtra: true,
    });
  }
  const total = segments.reduce((s, d) => s + (d.value || 0), 0);
  // Compute pct per segment relative to the (extended) total
  segments.forEach((seg) => {
    seg.pct = total > 0 ? +((seg.value / total) * 100).toFixed(1) : 0;
  });

  return (
    <div className="flex flex-col gap-4 h-full" data-testid="theme-portfolio-bars">
      {/* Single stacked bar */}
      <div className="h-7 w-full rounded-lg overflow-hidden flex shadow-inner bg-slate-100" data-testid="theme-stacked-bar">
        {segments.map((d) => {
          const w = total > 0 ? ((d.value || 0) / total) * 100 : 0;
          if (w <= 0) return null;
          return (
            <div
              key={d.theme}
              className="h-full relative group transition-all duration-300 hover:brightness-110"
              style={{ width: `${w}%`, backgroundColor: d.color }}
              data-testid={`theme-segment-${d.theme}`}
              title={`${d.isExtra ? d.theme : displayTheme(d.theme)}: ${d.pct}% · ₹${d.value.toFixed(2)} Cr`}
            >
              {w >= 6 && (
                <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white drop-shadow"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.35)" }}>
                  {d.pct}%
                </span>
              )}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover:block bg-slate-800 text-white text-[10px] rounded-md px-2.5 py-1.5 shadow-lg z-30 whitespace-nowrap pointer-events-none">
                <span className="font-bold block">{d.isExtra ? d.theme : displayTheme(d.theme)}</span>
                <span className="block opacity-80">₹{d.value.toFixed(2)} Cr · {d.pct}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend — flex-wrap so it auto-expands to fit Strategic Initiatives chip */}
      <div className="grid grid-cols-5 gap-x-3 gap-y-1.5">
        {segments.map((d) => (
          <div
            key={d.theme}
            className="flex items-center gap-1.5 text-[11px] min-w-0"
            title={`${d.isExtra ? d.theme : displayTheme(d.theme)} · ₹${d.value.toFixed(2)} Cr · ${d.pct}%`}
            data-testid={`theme-legend-${d.theme}`}
          >
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: d.color }} />
            <span className={`font-medium truncate flex-1 ${d.isExtra ? "text-[#7F1D1D] font-semibold" : "text-slate-700"}`}>
              {d.isExtra ? d.theme : displayTheme(d.theme)}
            </span>
            <span className="font-semibold text-slate-900 tabular-nums flex-shrink-0">{d.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}
